import { all, call, put, retry, select, takeEvery, takeLatest } from 'redux-saga/effects';
import { calendarActions, siteActions } from '../actions';
import { EventListActionType, EventList } from '../modules/calendar/types';
import ACTIONS from '../app.constants';
import { getAccessToken } from './authentication.sagas';
import { Api, API_CONFIGURATIONS } from '../services/services';
import { AppointmentLicenses } from './../modules/application/types';
import { getAppointmentPersonList } from '../selectors/person.selectors';
import { DownloadTemplateDocumentActionType, Form } from '../modules/application/types';
import { getCalendarSearchFormId } from '../selectors/site.selectors';
import defaultSearchForm from '../../views/Calendar/defaultSearchForm.json';

function* getEventList(action: EventListActionType) {
    try {
        yield put(calendarActions.getEventList.pending(action.data));
        const token: string = yield call(getAccessToken, 'events.read');

        // TODO: Remove after hotfix. This is necessary to avoid jerkiness when
        // loading events.
        yield put(calendarActions.getEventListCount.success({ totalEventCount: 0 }));

        const response: EventList = yield retry(
            API_CONFIGURATIONS.MAX_RETRY_COUNT,
            API_CONFIGURATIONS.RETRY_INTERVAL,
            Api.getEventList,
            token,
            action.data,
            action?.payload?.data,
        );

        yield put(calendarActions.getEventList.success(response, action.data.limit, action.data.skip));
    } catch (error) {
        yield put(siteActions.setError(error));
        yield put(calendarActions.getEventList.error(error));
    }
}

function* applyFilter(action: EventListActionType) {
    yield getEventList(action);
}

function* getCalendarSearchForm() {
    try {
        let searchForm: Form = defaultSearchForm;
        yield put(calendarActions.getCalendarSearchForm.pending);
        const token: string = yield call(getAccessToken, 'forms.read');
        const formId: string = yield select(getCalendarSearchFormId);
        if (!!formId) {
            try {
                searchForm = yield retry(
                    API_CONFIGURATIONS.MAX_RETRY_COUNT,
                    API_CONFIGURATIONS.RETRY_INTERVAL,
                    Api.getForm,
                    token,
                    formId,
                );
            } catch (error) {
                yield put(calendarActions.getCalendarSearchForm.error(error));
            }
        }
        yield put(calendarActions.getCalendarSearchForm.success(searchForm));
    } catch (error) {
        yield put(siteActions.setError(error));
    }
}

function* mergeInspectorPackDocuments(action: DownloadTemplateDocumentActionType) {
    const templateId = action.payload.templateId;
    const fileName = action.payload.fileName;
    const isInspectorPack = action.payload.isInspectorPack;
    const listFilters = action.payload.listFilters;
    try {
        yield put(calendarActions.mergeInspectorPackDocuments.pending);
        const token: string = yield call(getAccessToken, 'events.read');
        const result: { response: string; status: number } = yield retry(
            API_CONFIGURATIONS.MAX_RETRY_COUNT,
            API_CONFIGURATIONS.RETRY_INTERVAL,
            Api.mergeDocuments,
            token,
            templateId,
            fileName,
            '',
            isInspectorPack,
            listFilters,
            [],
            '',
        );
        yield put(calendarActions.mergeInspectorPackDocuments.success(result));
        if (result.status === 200) {
            yield put(siteActions.setSuccess({ isError: false, errorCode: 0, message: result.response }));
        } else {
            yield put(siteActions.setError({ isError: true, errorCode: result.status, message: result.response }));
        }
    } catch (error) {
        yield put(siteActions.setError(error));
        yield put(calendarActions.mergeInspectorPackDocuments.error(error));
    }
}

function* getLinkedLicensesForAppointments(action: EventListActionType) {
    const id: string = action.payload.data;
    try {
        yield put(calendarActions.getLinkedLicensesForAppointments.pending);
        const token: string = yield call(getAccessToken, 'events.read');

        const licensesList: AppointmentLicenses = yield retry(
            API_CONFIGURATIONS.MAX_RETRY_COUNT,
            API_CONFIGURATIONS.RETRY_INTERVAL,
            Api.getLinkedLicensesForAppointments,
            token,
            id,
        );

        yield put(calendarActions.getLinkedLicensesForAppointments.success(licensesList));
    } catch (error) {
        yield put(siteActions.setError(error));
        yield put(calendarActions.getLinkedLicensesForAppointments.error(error));
    }
}

function* getFilteredLicensesForAppointments() {
    try {
        yield put(calendarActions.getFilteredLicensesForAppointment.pending);
        const token: string = yield call(getAccessToken, 'licenses.read');

        const personIds: [] = yield select(getAppointmentPersonList);
        const licensesList: AppointmentLicenses = yield retry(
            API_CONFIGURATIONS.MAX_RETRY_COUNT,
            API_CONFIGURATIONS.RETRY_INTERVAL,
            Api.getFilteredLicensesForAppointment,
            token,
            personIds.map((person: { id: string }) => person.id),
        );

        yield put(calendarActions.getFilteredLicensesForAppointment.success(licensesList));
    } catch (error) {
        yield put(siteActions.setError(error));
        yield put(calendarActions.getFilteredLicensesForAppointment.error(error));
    }
}

function* addLicenseForAppointment(action: EventListActionType) {
    const { selectedLicenses, appointmentId } = action.payload.data;
    try {
        yield put(calendarActions.addLicensesForAppointments.pending);
        const token: string = yield call(getAccessToken, 'events.write');
        const licensesList: AppointmentLicenses = yield retry(
            API_CONFIGURATIONS.MAX_RETRY_COUNT,
            API_CONFIGURATIONS.RETRY_INTERVAL,
            Api.addLicenseForAppointment,
            token,
            appointmentId,
            selectedLicenses,
        );

        yield put(calendarActions.addLicensesForAppointments.success(licensesList));
        yield put(
            siteActions.setSuccess({
                isError: false,
                errorCode: 0,
                message: ' Licenses were linked successfully to the appointment',
            }),
        );
        yield put(calendarActions.getLinkedLicensesBegin(appointmentId));
    } catch (error) {
        yield put(siteActions.setError(error));
        yield put(calendarActions.addLicensesForAppointments.error(error));
    }
}

function* removeLicenseFromAppointment(action: EventListActionType) {
    const { appointmentId, licenseId } = action.payload.data;
    try {
        yield put(calendarActions.removeLicenseFromAppointment.pending);
        const token: string = yield call(getAccessToken, 'events.write');
        yield retry(
            API_CONFIGURATIONS.MAX_RETRY_COUNT,
            API_CONFIGURATIONS.RETRY_INTERVAL,
            Api.removeLicenseFromAppointment,
            token,
            appointmentId,
            licenseId,
        );
        yield put(calendarActions.removeLicenseFromAppointment.success(licenseId));
        yield put(calendarActions.getLinkedLicensesBegin(appointmentId));
    } catch (error) {
        yield put(calendarActions.removeLicenseFromAppointment.error(error));
    }
}
export default function* rootSaga() {
    yield all([
        takeEvery(ACTIONS.GET_EVENT_LIST_BEGIN, getEventList),
        takeLatest(ACTIONS.APPLY_EVENT_FILTERS, applyFilter),
        takeEvery(ACTIONS.DOWNLOAD_INSPECTOR_PACKET_BEGIN, mergeInspectorPackDocuments),
        takeEvery(ACTIONS.GET_LINKED_LICENSES_FOR_APPOINTMENT_BEGIN, getLinkedLicensesForAppointments),
        takeEvery(ACTIONS.ADD_LICENSES_FOR_APPOINTMENT_BEGIN, addLicenseForAppointment),
        takeEvery(ACTIONS.GET_FILTERED_LICENSES_FOR_APPOINTMENT_BEGIN, getFilteredLicensesForAppointments),
        takeEvery(ACTIONS.REMOVE_LICENSE_FROM_APPOINTMENT_BEGIN, removeLicenseFromAppointment),
        takeEvery(ACTIONS.GET_CALENDAR_SEARCH_FORM_BEGIN, getCalendarSearchForm),
    ]);
}
